import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import "./index.css";
import "./App.css";
import LocationDropdown from "./components/LocationDropdown";
import InternationalPhoneInput from "./components/InternationalPhoneInput";

const API_URL = process.env.REACT_APP_API_URL;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_URL}${url}`;
};

export function fetchWithTimeout(url, opts = {}, ms = 6000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...opts, signal: controller.signal }).finally(() => clearTimeout(timer));
}

function normalizeE164Phone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return null;
  return `+${digits}`;
}

function isGoogleMapsLink(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("google.com/maps") ||
    text.includes("maps.app.goo.gl") ||
    text.includes("goo.gl/maps")
  );
}

function App() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [customLocation, setCustomLocation] = useState("");
  const [menu, setMenu] = useState([]);
  const [error, setError] = useState("");
  const [lastOrderToken, setLastOrderToken] = useState(null);

  // ── Cart state ──────────────────────────────────────────────
  // cartItems: [{ item: string, quantity: number }]
  const [cartItems, setCartItems] = useState([]);
  const [pendingItem, setPendingItem] = useState("");
  const [pendingQty, setPendingQty] = useState(1);
  // ────────────────────────────────────────────────────────────

  const [proofFile, setProofFile] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);

  const [formStatus, setFormStatus] = useState(null);
  const [hasExistingOrders, setHasExistingOrders] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [lastPointsEarned, setLastPointsEarned] = useState(0);
  const [lastStreakBonusPoints, setLastStreakBonusPoints] = useState(0);
  const [lastLoyaltyTotal, setLastLoyaltyTotal] = useState(null);
  const [lastDiscountAmount, setLastDiscountAmount] = useState(0);
  const [lastClaimedDiscountAmount, setLastClaimedDiscountAmount] = useState(0);
  const [claimedDiscounts, setClaimedDiscounts] = useState([]);
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);

  const navigate = useNavigate();

  const fetchMenu = useCallback(async (sessionId = null) => {
    try {
      const menuUrl = sessionId
        ? `${API_URL}/menu?session_id=${encodeURIComponent(sessionId)}`
        : `${API_URL}/menu`;
      const res = await fetchWithTimeout(menuUrl, { credentials: "include" });
      const data = await res.json();
      setMenu(data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  }, []);

  const fetchQr = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/settings/duitnow_qr`);
      const data = await res.json();
      if (data.value) setQrUrl(data.value);
    } catch (err) {
      console.error("Error fetching QR:", err);
    }
  }, []);

  const fetchFormStatus = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/form-status`);
      const data = await res.json();
      setFormStatus(data);
      return data;
    } catch (err) {
      setFormStatus({ is_open: false, reason: "closed" });
      return null;
    }
  }, []);

  const checkExistingOrders = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/orders/mine`, { credentials: "include" });
      const data = await res.json();
      setHasExistingOrders(data.length > 0);
    } catch (err) {
      setHasExistingOrders(false);
    }
  }, []);

  const fetchAuthSession = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/auth/me`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data?.authenticated || !data?.user) {
        setAuthUser(null);
        setLoyalty(null);
        return;
      }

      setAuthUser(data.user);
      setLoyalty(data.loyalty || null);
      setName(data.user.name || "");
      setPhone(data.user.phone || "");
      setLocation(data.user.default_location || "");
    } catch (_) {
      setAuthUser(null);
      setLoyalty(null);
    }
  }, []);

  const fetchClaimedDiscounts = useCallback(async () => {
    if (!authUser) {
      setClaimedDiscounts([]);
      setSelectedClaimIds([]);
      return;
    }
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/claims?status=claimed`, { credentials: "include" });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setClaimedDiscounts(data);
        setSelectedClaimIds((prev) => prev.filter((id) => data.some((row) => Number(row.id) === Number(id))));
      } else {
        setClaimedDiscounts([]);
        setSelectedClaimIds([]);
      }
    } catch (_) {
      setClaimedDiscounts([]);
      setSelectedClaimIds([]);
    }
  }, [authUser]);

  useEffect(() => {
    const loadInitialData = async () => {
      const status = await fetchFormStatus();

      await Promise.allSettled([
        fetchLocations(),
        checkExistingOrders(),
        fetchQr(),
        fetchAuthSession(),
      ]);

      if (status?.is_open) {
        await fetchMenu(status.session_id || null);
      } else {
        setMenu([]);
      }
    };

    loadInitialData();
  }, [fetchFormStatus, fetchMenu, fetchLocations, checkExistingOrders, fetchQr, fetchAuthSession]);

  useEffect(() => {
    fetchClaimedDiscounts();
  }, [fetchClaimedDiscounts]);

  // ── Cart helpers ─────────────────────────────────────────────

  const itemAvailability = formStatus?.item_availability || {};

  /** How many of `itemName` are still available after accounting for what's already in the cart */
  // eslint-disable-next-line no-unused-vars
  const getRemainingForItem = (itemName) => {
    const avail = itemAvailability[itemName];
    const inCart = cartItems.find((c) => c.item === itemName)?.quantity || 0;
    if (!avail) {
      // No per-item limit — use overall spots remaining
      const spotsLeft = (formStatus?.max_orders || 0) - (formStatus?.order_count || 0);
      const totalInCart = cartItems.reduce((s, c) => s + c.quantity, 0);
      return Math.max(0, spotsLeft - totalInCart + inCart);
    }
    return Math.max(0, avail.remaining - inCart + inCart); // remaining is server-side already
  };

  /** Max qty user can add to the pending slot for `itemName` */
  const maxPendingQty = (itemName) => {
    if (!itemName) return 1;
    const avail = itemAvailability[itemName];
    const alreadyInCart = cartItems.find((c) => c.item === itemName)?.quantity || 0;
    if (!avail) {
      const spotsLeft = (formStatus?.max_orders || 0) - (formStatus?.order_count || 0);
      const totalInCart = cartItems.reduce((s, c) => s + c.quantity, 0);
      return Math.max(1, spotsLeft - totalInCart);
    }
    return Math.max(1, avail.remaining - alreadyInCart);
  };

  const addToCart = () => {
    if (!pendingItem) return setError("Please select a menu item to add.");
    const avail = itemAvailability[pendingItem];
    if (avail?.sold_out) return setError(`${pendingItem} is sold out.`);

    const max = maxPendingQty(pendingItem);
    if (pendingQty < 1 || pendingQty > max) return setError(`You can add at most ${max} of ${pendingItem}.`);

    setCartItems((prev) => {
      const existing = prev.find((c) => c.item === pendingItem);
      if (existing) {
        // Merge: increase quantity
        return prev.map((c) =>
          c.item === pendingItem ? { ...c, quantity: c.quantity + pendingQty } : c
        );
      }
      return [...prev, { item: pendingItem, quantity: pendingQty }];
    });

    setPendingItem("");
    setPendingQty(1);
    setError("");
  };

  const removeFromCart = (itemName) => {
    setCartItems((prev) => prev.filter((c) => c.item !== itemName));
  };

  const updateCartQty = (itemName, newQty) => {
    const avail = itemAvailability[itemName];
    const maxQty = avail ? avail.remaining : (formStatus?.max_orders || 99);
    const clamped = Math.max(1, Math.min(newQty, maxQty));
    setCartItems((prev) =>
      prev.map((c) => (c.item === itemName ? { ...c, quantity: clamped } : c))
    );
  };
  // ────────────────────────────────────────────────────────────

  const submitOrder = async (e) => {
    e.preventDefault();
    setLastDiscountAmount(0);
    setLastClaimedDiscountAmount(0);
    setLastStreakBonusPoints(0);

    const normalizedPhone = normalizeE164Phone(phone);
    if (cartItems.length === 0) return setError("Please add at least one item to your order.");

    if (authUser) {
      if (!name.trim()) return setError("Your account is missing your name. Please update it in Account.");
      if (!normalizedPhone) {
        return setError("Your account phone looks invalid. Please fix it in Account before ordering.");
      }
      if (!location) {
        return setError("Your account has no default location. Please set it in Account before ordering.");
      }
    } else {
      if (!name.trim()) return setError("Name cannot be empty.");
      if (!normalizedPhone) {
        return setError("Please enter a valid WhatsApp number.");
      }
      if (!location) return setError("Please select a delivery location.");
      if ((location === "OTHER" || location === "MAPS_LINK") && !customLocation.trim()) {
        return setError("Please paste your Google Maps link or type your full address.");
      }
      if (location === "MAPS_LINK" && !isGoogleMapsLink(customLocation)) {
        return setError("Please paste a valid Google Maps link (google.com/maps or maps.app.goo.gl).");
      }
    }

    if (requiresPaymentProof && !proofFile) {
      return setError("Please upload your payment proof before placing the order.");
    }

    setError("");

    try {
      const formData = new FormData();
      const finalLocation = (location === "OTHER" || location === "MAPS_LINK") ? customLocation : location;
      formData.append("name", name);
      formData.append("phone", normalizedPhone || "");
      formData.append("location", finalLocation);
      formData.append("items", JSON.stringify(cartItems)); // NEW: array of { item, quantity }
      if (proofFile) {
        formData.append("proof", proofFile);
      }
      formData.append("claimed_discount_ids", JSON.stringify(selectedClaimIds));

      const res = await fetch(`${API_URL}/orders`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403) await fetchFormStatus();
        setError(data.error || "Failed to submit order.");
        return;
      }

      setLastOrderToken(data.order_token);
      setLastPointsEarned(Number(data.points_earned || 0));
      setLastStreakBonusPoints(Number(data.streak_bonus_points || 0));
      setLastDiscountAmount(Number(data.discount_amount || 0));
      setLastClaimedDiscountAmount(Number(data.claimed_discount_amount || 0));
      if (data.loyalty) {
        setLoyalty(data.loyalty);
      }
      setLastLoyaltyTotal(
        data.loyalty_points_total !== null && data.loyalty_points_total !== undefined
          ? Number(data.loyalty_points_total)
          : null
      );

      if (authUser) {
        setName(authUser.name || "");
        setPhone(authUser.phone || "");
        setLocation(authUser.default_location || "");
      } else {
        setName("");
        setPhone("");
        setLocation("");
      }

      setCartItems([]);
      setPendingItem("");
      setPendingQty(1);
      setCustomLocation("");
      setProofFile(null);
      setSelectedClaimIds([]);

      if (authUser) {
        await Promise.allSettled([fetchAuthSession(), fetchClaimedDiscounts()]);
      }

      await fetchFormStatus();
    } catch (err) {
      setError("Failed to submit order.");
    }
  };

  const handlePlaceAnother = async () => {
    setLastOrderToken(null);
    setProofFile(null);
    setLastStreakBonusPoints(0);
    await fetchFormStatus();
  };

  /* ===== Loading ===== */
  if (formStatus === null) {
    return (
      <div className="mama-page" style={pageStyle}>
        <Navbar />
        <div className="mama-main" style={mainContentStyle}>
          <div className="mama-card" style={cardStyle}>
            <p style={{ textAlign: "center", color: "var(--text-muted)" }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  /* ===== Form Closed / Full ===== */
  if (!formStatus.is_open) {
    const isFull = formStatus.reason === "full";
    return (
      <div className="mama-page" style={pageStyle}>
        <Navbar />
        <div className="mama-main" style={mainContentStyle}>
          <div className="mama-card mama-closed-card" style={cardStyle}>
            <div className="mama-closed-emoji">{isFull ? "🎉" : "🙏"}</div>
            <h2 className="mama-heading" style={{ marginBottom: "0.5rem" }}>
              {isFull ? "We're fully booked!" : "Orders are closed for now"}
            </h2>
            <p style={{ color: "var(--text-muted)", lineHeight: 1.6, maxWidth: "340px", margin: "0 auto" }}>
              {isFull
                ? "We've reached our maximum orders for this session. Thank you for your interest — see you next time! 🍱"
                : "The order form isn't open right now. Check back soon — we'd love to cook for you! 🍱"}
            </p>
            {hasExistingOrders && (
              <button
                onClick={() => navigate("/track")}
                className="mama-btn-primary"
                style={{ marginTop: "1.5rem" }}
              >
                Track My Existing Orders →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ===== Per-item availability ===== */
  const hasItemLimits = Object.keys(itemAvailability).length > 0;

  const totalMax = hasItemLimits
    ? Object.values(itemAvailability).reduce((s, i) => s + i.max, 0)
    : formStatus.max_orders;
  const totalOrdered = hasItemLimits
    ? Object.values(itemAvailability).reduce((s, i) => s + i.ordered, 0)
    : formStatus.order_count;
  const capacityPct = Math.min((totalOrdered / totalMax) * 100, 100);
  const spotsLeft = totalMax - totalOrdered;

  // For order summary computation
  const totalCartQty = cartItems.reduce((s, c) => s + c.quantity, 0);
  const selectedLoc = locations.find((l) => l.name === location);
  const isCustomLocation = location === "OTHER" || location === "MAPS_LINK";
  const isMapsLinkLocation = location === "MAPS_LINK";
  const deliveryFee = selectedLoc ? parseFloat(selectedLoc.delivery_fee) : 0;
  const itemsTotal = cartItems.reduce((sum, cartItem) => {
    const menuItem = menu.find((m) => m.name === cartItem.item);
    return sum + (menuItem ? parseFloat(menuItem.price) * cartItem.quantity : 0);
  }, 0);
  const grandTotal = itemsTotal + deliveryFee;
  const welcomeDiscountPreview = authUser && !authUser?.welcome_offer_used ? 3 : 0;
  const selectedClaimDiscountTotal = claimedDiscounts
    .filter((claim) => selectedClaimIds.includes(Number(claim.id)))
    .reduce((sum, claim) => sum + Number(claim.discount_amount || 0), 0);
  const totalDiscountPreview = Math.min(grandTotal, welcomeDiscountPreview + selectedClaimDiscountTotal);
  const netTotalPreview = Math.max(0, grandTotal - totalDiscountPreview);
  const requiresPaymentProof = netTotalPreview > 0;

  // Items available to add (not already at their limit in the cart)
  const addableMenuItems = menu.filter((m) => {
    const avail = itemAvailability[m.name];
    if (avail?.sold_out) return false;
    if (avail) {
      const inCart = cartItems.find((c) => c.item === m.name)?.quantity || 0;
      return inCart < avail.remaining;
    }
    return true;
  });

  return (
    <div className="mama-page" style={pageStyle}>
      <Navbar />
      <div className="mama-main" style={mainContentStyle}>
        <div className="mama-card" style={cardStyle}>
          <h1 className="mama-heading">Place Your Order</h1>
          <p className="mama-subheading">Fresh homemade béchamel, made just for you 🧡</p>

          {authUser ? (
            <p style={{ marginTop: "-0.35rem", marginBottom: "1rem", fontSize: "0.82rem", color: "var(--success)" }}>
              Account mode enabled: details are auto-filled. Loyalty points: {loyalty?.points || 0}.
              {!authUser?.welcome_offer_used && " Welcome offer: RM3 off your first order is active."}
            </p>
          ) : (
            <div
              style={{
                marginTop: "-0.2rem",
                marginBottom: "1rem",
                border: "1px dashed var(--border)",
                borderRadius: "10px",
                padding: "0.6rem 0.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
              }}
            >
              <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                Want faster checkout and rewards?
              </span>
              <Link to="/account" className="mama-btn-secondary" style={{ whiteSpace: "nowrap", padding: "0.45rem 0.75rem", fontSize: "0.82rem" }}>
                Create account
              </Link>
            </div>
          )}

          {/* Capacity bar */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.82rem",
                color: spotsLeft <= 5 ? "var(--error)" : "var(--text-muted)",
                marginBottom: "0.4rem",
                fontWeight: 500,
              }}
            >
              <span>
                {spotsLeft <= 5
                  ? `⚠️ Only ${spotsLeft} spot${spotsLeft === 1 ? "" : "s"} left!`
                  : `${spotsLeft} spots remaining`}
              </span>
              <span>
                {totalOrdered} / {totalMax}
              </span>
            </div>
            <div className="mama-capacity-track">
              <div
                className="mama-capacity-fill"
                style={{
                  width: `${capacityPct}%`,
                  background: spotsLeft <= 5 ? "var(--error)" : "var(--orange)",
                }}
              />
            </div>
          </div>

          {lastOrderToken ? (
            /* ===== Success Screen ===== */
            <div className="mama-success">
              <h2
                style={{
                  margin: 0,
                  color: "var(--success)",
                  fontFamily: "'Playfair Display', Georgia, serif",
                }}
              >
                ✅ Order placed!
              </h2>
              <p style={{ marginTop: "0.75rem", color: "var(--text-main)" }}>Your Order ID:</p>
              <div className="mama-token">{lastOrderToken}</div>
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                Screenshot this just in case — your order also loads automatically on this device.
              </p>
              {authUser && (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.85rem", color: "var(--success)", fontWeight: 700 }}>
                  🌟 You earned {lastPointsEarned} points
                  {lastStreakBonusPoints > 0 ? ` (🔥 +${lastStreakBonusPoints} streak bonus)` : ""}
                  {lastLoyaltyTotal !== null ? ` • Total: ${lastLoyaltyTotal}` : ""}
                  {lastDiscountAmount > 0 ? ` • RM ${lastDiscountAmount.toFixed(2)} total discount applied` : ""}
                  {lastClaimedDiscountAmount > 0 ? ` (RM ${lastClaimedDiscountAmount.toFixed(2)} from claimed rewards)` : ""}
                </p>
              )}
              {authUser && loyalty && (
                <p style={{ margin: "0.45rem 0 0", fontSize: "0.8rem", color: "var(--brown-mid)", fontWeight: 700 }}>
                  {loyalty.current_streak > 0
                    ? `🔥 Streak: ${loyalty.current_streak} week${loyalty.current_streak === 1 ? "" : "s"} • Next streak bonus: +${loyalty.streak_bonus_next_order || 0}`
                    : "Start your streak this week and keep ordering weekly for bonus points."}
                </p>
              )}
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem", flexWrap: "wrap" }}>
                <Link to="/track" className="mama-btn-primary">
                  Track My Order →
                </Link>
                <button onClick={handlePlaceAnother} className="mama-btn-secondary">
                  Place Another Order
                </button>
              </div>
            </div>
          ) : (
            /* ===== Order Form ===== */
            <form onSubmit={submitOrder}>
              {authUser ? (
                <div
                  style={{
                    marginBottom: "1rem",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "0.75rem",
                    background: "var(--beige)",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 700, color: "var(--text-main)" }}>Checkout details saved</p>
                  <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                    {name || "No name"} • {phone || "No phone"}
                  </p>
                  <p style={{ margin: "0.2rem 0 0", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                    Delivery: {location || "No default location set"}
                  </p>
                  {!location && (
                    <p style={{ margin: "0.45rem 0 0", fontSize: "0.82rem", color: "var(--error)", fontWeight: 600 }}>
                      Please set your default location in Account before placing an order.
                    </p>
                  )}
                  {loyalty && (
                    <p style={{ margin: "0.45rem 0 0", fontSize: "0.82rem", color: "var(--brown-mid)", fontWeight: 700 }}>
                      {loyalty.current_streak > 0
                        ? `🔥 ${loyalty.current_streak}-week streak active • Next streak bonus +${loyalty.streak_bonus_next_order || 0} pts`
                        : "Order this week to begin your weekly streak and unlock bonus points."}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <label className="mama-label">Your Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mama-input" />

                  <label className="mama-label">WhatsApp Number</label>
                  <InternationalPhoneInput
                    id="guest-order-phone"
                    value={phone}
                    onChange={setPhone}
                    placeholder="Enter your number"
                  />
                  <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "-0.75rem 0 1rem" }}>
                    📱 Country code picker included. Enter number without leading 0.
                  </p>
                </>
              )}

              {/* ===== Cart Builder ===== */}
              <label className="mama-label">Your Order</label>

              {/* Existing cart items */}
              {cartItems.length > 0 && (
                <div className="mama-cart-list">
                  {cartItems.map((cartItem) => {
                    const menuItem = menu.find((m) => m.name === cartItem.item);
                    const avail = itemAvailability[cartItem.item];
                    const maxQty = avail ? avail.remaining : spotsLeft;
                    return (
                      <div key={cartItem.item} className="mama-cart-row">
                        <div className="mama-cart-row-name">
                          <span className="mama-cart-emoji">🍱</span>
                          <span>{cartItem.item}</span>
                          {menuItem && (
                            <span className="mama-cart-price">RM {parseFloat(menuItem.price).toFixed(2)} ea</span>
                          )}
                        </div>
                        <div className="mama-cart-row-controls">
                          <button
                            type="button"
                            className="mama-qty-btn"
                            onClick={() => updateCartQty(cartItem.item, cartItem.quantity - 1)}
                            disabled={cartItem.quantity <= 1}
                          >
                            −
                          </button>
                          <span className="mama-qty-display">{cartItem.quantity}</span>
                          <button
                            type="button"
                            className="mama-qty-btn"
                            onClick={() => updateCartQty(cartItem.item, cartItem.quantity + 1)}
                            disabled={cartItem.quantity >= maxQty}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            className="mama-cart-remove"
                            onClick={() => removeFromCart(cartItem.item)}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add item row */}
              {addableMenuItems.length > 0 && (
                <div className="mama-add-item-row">
                  <select
                    value={pendingItem}
                    onChange={(e) => { setPendingItem(e.target.value); setPendingQty(1); }}
                    className="mama-select mama-add-item-select"
                  >
                    <option value="">Add an item…</option>
                    {addableMenuItems.map((m) => {
                      const avail = itemAvailability[m.name];
                      const remaining = avail ? avail.remaining : null;
                      const inCart = cartItems.find((c) => c.item === m.name)?.quantity || 0;
                      const netRemaining = remaining !== null ? remaining - inCart : null;
                      return (
                        <option key={m.id} value={m.name}>
                          {m.name} — RM {m.price}
                          {netRemaining !== null ? ` (${netRemaining} left)` : ""}
                        </option>
                      );
                    })}
                  </select>

                  <input
                    type="number"
                    min="1"
                    max={maxPendingQty(pendingItem)}
                    value={pendingQty}
                    onChange={(e) => setPendingQty(Number(e.target.value))}
                    className="mama-input mama-add-item-qty"
                    disabled={!pendingItem}
                  />

                  <button
                    type="button"
                    className="mama-btn-add-item"
                    onClick={addToCart}
                    disabled={!pendingItem}
                  >
                    + Add
                  </button>
                </div>
              )}

              {cartItems.length === 0 && (
                <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0.25rem 0 1.25rem" }}>
                  👆 Select an item above and click "+ Add" to build your order.
                </p>
              )}

              {!authUser && (
                <>
                  <label className="mama-label" style={{ marginTop: "1.25rem" }}>Delivery Location</label>
                  <LocationDropdown
                    id="order-location-dropdown"
                    value={location}
                    onChange={setLocation}
                    locations={locations}
                    mapsLinkLabel="📍 Paste Google Maps link (best for outside service area)"
                    otherLabel="🏠 Type full address manually"
                  />
                  {(location === "OTHER" || location === "MAPS_LINK") && (
                    <>
                      <input
                        value={customLocation}
                        onChange={(e) => setCustomLocation(e.target.value)}
                        className="mama-input"
                        placeholder={isMapsLinkLocation ? "Paste your Google Maps link here" : "Type your full address here"}
                        style={{ marginTop: "0.75rem" }}
                      />
                      {isMapsLinkLocation ? (
                        <p style={{ fontSize: "0.82rem", color: "var(--brown-mid)", margin: "0.25rem 0 1rem", fontWeight: 700 }}>
                          Paste the full Google Maps link (example: https://maps.app.goo.gl/...).
                        </p>
                      ) : (
                        <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0.25rem 0 1rem" }}>
                          Tip: Include building/block/room details so delivery is accurate.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ===== Order Summary ===== */}
              {cartItems.length > 0 && location && (
                <>
                  {authUser && claimedDiscounts.length > 0 && (
                    <div className="mama-summary" style={{ marginBottom: "0.8rem" }}>
                      <p className="mama-summary-heading">Claimed discounts</p>
                      <p style={{ marginTop: "0.2rem", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        Select one or multiple claimed rewards to apply on this order.
                      </p>
                      {claimedDiscounts.map((claim) => {
                        const checked = selectedClaimIds.includes(Number(claim.id));
                        return (
                          <label key={claim.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginTop: "0.45rem", fontSize: "0.84rem", color: "var(--text-main)" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const id = Number(claim.id);
                                  setSelectedClaimIds((prev) =>
                                    e.target.checked ? [...prev, id] : prev.filter((v) => v !== id)
                                  );
                                }}
                              />
                              <span>{claim.offer_name || "Loyalty reward"}</span>
                            </span>
                            <strong style={{ color: "var(--success)" }}>- RM {Number(claim.discount_amount || 0).toFixed(2)}</strong>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className="mama-summary">
                    <p className="mama-summary-heading">Order Summary</p>
                    {cartItems.map((cartItem) => {
                      const menuItem = menu.find((m) => m.name === cartItem.item);
                      const lineTotal = menuItem ? parseFloat(menuItem.price) * cartItem.quantity : 0;
                      return (
                        <div className="mama-summary-row" key={cartItem.item}>
                          <span>
                            {cartItem.quantity}× {cartItem.item}
                          </span>
                          <span>RM {lineTotal.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div className="mama-summary-row">
                      <span>Delivery to {isCustomLocation ? customLocation || "custom address" : location}</span>
                      <span>
                        {isCustomLocation
                          ? "📍 Fee TBC by admin"
                          : deliveryFee === 0
                          ? "Free"
                          : `RM ${deliveryFee.toFixed(2)}`}
                      </span>
                    </div>
                    {totalDiscountPreview > 0 && (
                      <div className="mama-summary-row" style={{ color: "var(--success)", fontWeight: 700 }}>
                        <span>Discounts applied</span>
                        <span>- RM {totalDiscountPreview.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="mama-summary-total">
                      <span>
                        Total ({totalCartQty} item{totalCartQty !== 1 ? "s" : ""})
                      </span>
                      <span>RM {netTotalPreview.toFixed(2)}</span>
                    </div>
                    {authUser && !authUser?.welcome_offer_used && (
                      <p style={{ marginTop: "0.45rem", marginBottom: 0, fontSize: "0.78rem", color: "var(--orange)" }}>
                        RM3 first-order welcome discount will be auto-applied.
                      </p>
                    )}
                  </div>

                  {/* ===== Pay first section ===== */}
                  <div className="mama-duitnow">
                    <p className="mama-duitnow-heading">
                      {requiresPaymentProof ? "💳 Step: Pay via DuitNow first" : "🎉 Free order unlocked"}
                    </p>

                    {requiresPaymentProof && qrUrl ? (
                      <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
                        <img
                          src={resolveUrl(qrUrl)}
                          alt="DuitNow QR"
                          style={{
                            maxWidth: "180px",
                            border: "1px solid var(--border)",
                            borderRadius: "12px",
                            padding: "0.4rem",
                            background: "#fff",
                          }}
                        />
                        <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                          Scan & pay <strong>RM {netTotalPreview.toFixed(2)}</strong>, then upload proof below
                        </p>
                      </div>
                    ) : requiresPaymentProof ? (
                      <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                        Payment QR not set up yet — contact admin.
                      </p>
                    ) : (
                      <p style={{ fontSize: "0.85rem", color: "var(--success)", marginBottom: "0.75rem", fontWeight: 700 }}>
                        Total is RM 0.00 after discounts. No payment proof needed.
                      </p>
                    )}

                    {requiresPaymentProof && (
                      <>
                        <label
                          className="mama-label"
                          style={{ textTransform: "none", fontSize: "0.88rem" }}
                        >
                          Upload payment proof <span style={{ color: "var(--error)" }}>*</span>
                          <span style={{ fontWeight: "normal", color: "var(--text-muted)", marginLeft: "0.3rem" }}>
                            (screenshot or PDF)
                          </span>
                        </label>
                        <div
                          className="mama-upload-zone"
                          onClick={() => document.getElementById("proof-upload-main").click()}
                        >
                          <input
                            id="proof-upload-main"
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => setProofFile(e.target.files[0])}
                            style={{ display: "none" }}
                          />
                          {proofFile ? (
                            <p className="mama-upload-ready">✅ {proofFile.name} — ready to submit</p>
                          ) : (
                            <>
                              <span className="mama-upload-icon">📎</span>
                              <span className="mama-upload-text">Tap to upload receipt</span>
                            </>
                          )}
                        </div>
                        {!proofFile && (
                          <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: 0 }}>
                            You must attach proof to place your order.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {error && <p className="mama-error">{error}</p>}

              <button
                type="submit"
                disabled={(requiresPaymentProof && !proofFile) || cartItems.length === 0}
                className="mama-btn-primary"
                style={{
                  width: "100%",
                  display: "block",
                  opacity: ((requiresPaymentProof && !proofFile) || cartItems.length === 0) ? 0.4 : 1,
                  cursor: ((requiresPaymentProof && !proofFile) || cartItems.length === 0) ? "not-allowed" : "pointer",
                  boxShadow:
                    ((requiresPaymentProof && !proofFile) || cartItems.length === 0) ? "none" : "0 4px 14px rgba(224,123,57,0.35)",
                }}
              >
                {cartItems.length === 0
                  ? "Add items to your order"
                  : requiresPaymentProof && !proofFile
                  ? "Upload payment proof to continue"
                  : `Place Order (${totalCartQty} item${totalCartQty !== 1 ? "s" : ""})`}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Shared Navbar ===== */
export function Navbar() {
  return (
    <>
      <nav className="mama-sidebar">
        <div className="mama-sidebar-brand">
          <div className="mama-logo-wrap">
            <img
              src={process.env.PUBLIC_URL + "/logo.png"}
              alt="Mama Bacamel"
              className="mama-logo-img"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "block";
              }}
            />
            <span className="mama-logo-fallback" style={{ display: "none" }}>
              🍝
            </span>
          </div>
          <p className="mama-tagline">cooked with love 🧡</p>
        </div>
        <div className="mama-nav-links">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `mama-nav-link ${isActive ? "active" : ""}`}
          >
            <span className="nav-emoji">📋</span> Order
          </NavLink>
          <NavLink
            to="/track"
            className={({ isActive }) => `mama-nav-link ${isActive ? "active" : ""}`}
          >
            <span className="nav-emoji">📦</span> Track
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) => `mama-nav-link ${isActive ? "active" : ""}`}
          >
            <span className="nav-emoji">💬</span> Contact
          </NavLink>
          <NavLink
            to="/account"
            className={({ isActive }) => `mama-nav-link ${isActive ? "active" : ""}`}
          >
            <span className="nav-emoji">👤</span> Account
          </NavLink>
        </div>
      </nav>

      <nav className="mama-bottom-nav">
        <NavLink to="/" end className={({ isActive }) => `mama-bottom-link ${isActive ? "active" : ""}`}>
          <span className="bottom-emoji">📋</span>
          <span>Order</span>
        </NavLink>
        <NavLink
          to="/track"
          className={({ isActive }) => `mama-bottom-link ${isActive ? "active" : ""}`}
        >
          <span className="bottom-emoji">📦</span>
          <span>Track</span>
        </NavLink>
        <NavLink
          to="/contact"
          className={({ isActive }) => `mama-bottom-link ${isActive ? "active" : ""}`}
        >
          <span className="bottom-emoji">💬</span>
          <span>Contact</span>
        </NavLink>
        <NavLink
          to="/account"
          className={({ isActive }) => `mama-bottom-link ${isActive ? "active" : ""}`}
        >
          <span className="bottom-emoji">👤</span>
          <span>Account</span>
        </NavLink>
      </nav>
    </>
  );
}

export default App;

/* ===== Shared Styles — exported for TrackOrder & Contact ===== */
export const pageStyle = {
  minHeight: "100vh",
  background: "var(--cream)",
  fontFamily: "'Lato', sans-serif",
};

export const mainContentStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "2.5rem 2rem",
};

export const cardStyle = {
  maxWidth: "520px",
  margin: "0 auto",
  background: "var(--warm-white)",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  padding: "2rem",
  boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)",
};

export const inputStyle = {
  display: "block",
  width: "100%",
  padding: "0.7rem 0.85rem",
  marginBottom: "1.25rem",
  borderRadius: "10px",
  border: "1.5px solid #E8D4B0",
  background: "#F2E0C0",
  fontSize: "0.95rem",
  fontFamily: "'Lato', sans-serif",
  color: "#3B2010",
  boxSizing: "border-box",
  outline: "none",
};

export const labelStyle = {
  display: "block",
  fontFamily: "'Lato', sans-serif",
  fontWeight: "600",
  textTransform: "uppercase",
  color: "#8B5E3C",
  fontSize: "0.85rem",
  letterSpacing: "0.04em",
  marginBottom: "0.4rem",
};

export const primaryButtonStyle = {
  display: "inline-block",
  background: "#E07B39",
  color: "white",
  border: "none",
  padding: "0.7rem 1.6rem",
  borderRadius: "10px",
  fontSize: "1rem",
  fontFamily: "'Lato', sans-serif",
  fontWeight: "700",
  cursor: "pointer",
  textDecoration: "none",
  textAlign: "center",
  boxShadow: "0 4px 14px rgba(224, 123, 57, 0.35)",
};

export const secondaryButtonStyle = {
  display: "inline-block",
  background: "transparent",
  color: "#8B5E3C",
  border: "1.5px solid #8B5E3C",
  padding: "0.65rem 1.5rem",
  borderRadius: "10px",
  fontSize: "1rem",
  fontFamily: "'Lato', sans-serif",
  fontWeight: "500",
  cursor: "pointer",
  textDecoration: "none",
  textAlign: "center",
};